/**
 * Uniquely identifying a material variant based on persistent ids.
 * @module
 */
import type { MasonryId } from "./masonryTypes";

export interface MaterialSrc {
  templateId: number;
  layerConfigId?: number;
  colorId?: number;
  keepPreviousLayers?: boolean;
  userDefinedName?: string;
  userDefinedCategory?: string;
}

/**
 * @param src MaterialSrc identifying the material
 * @returns A persistent cache key. Can be used to fetch cached files like material thumbnails, so the values are promised to be stable over time.
 */
export function materialCacheKey(src: MaterialSrc): string {
  const key = [src.templateId, src.layerConfigId, src.colorId];
  return key.join("_"); // URL-friendly delimiter
}

export function matchesMaterialCacheKeyFormat(key: string): boolean {
  return key.match(/^\d+_\d*_\d*$/) !== null;
}

export function materialSrcFromCacheKey(key: string): MaterialSrc {
  const [templateId, layerConfigId, colorId] = key.split("_");

  const materialSrc: MaterialSrc = { templateId: parseInt(templateId) };
  if (layerConfigId !== "") {
    materialSrc.layerConfigId = parseInt(layerConfigId);
  }
  if (colorId !== "") {
    materialSrc.colorId = parseInt(colorId);
  }

  return materialSrc;
}

export function materialSrcEquals(a: MaterialSrc | null | undefined, b: MaterialSrc | null | undefined): boolean {
  if (a === undefined || a === null || b === undefined || b === null) {
    return (a === undefined || a === null) === (b === undefined || b === null);
  }
  if (a.templateId !== b.templateId) {
    return false;
  }
  if (a.layerConfigId !== b.layerConfigId) {
    return false;
  }
  if (a.colorId !== b.colorId) {
    return false;
  }
  return true;
}

export function isMasonryId(matId: MaterialSrc | MasonryId | undefined): matId is MasonryId {
  return typeof matId === "number";
}

export function isValidDesignMaterialSrc(
  materialSrc: MaterialSrc | MasonryId | undefined | null,
): materialSrc is MaterialSrc {
  return materialSrc !== undefined && materialSrc !== null && (materialSrc as MaterialSrc).templateId !== undefined;
}
